import { useSplitTests } from '@api/lendingAPI/lapiHooks';
import type { definitions } from '@iwoca/lapi-client/edge';
import { Text } from '@ui';
import { defineMessages, FormattedMessage } from 'react-intl';

import { FormattedName } from '../../../iwIntlProvider/FormattedName';
import { CheckShieldSVG } from '../../../svgComponents/checkShieldSVG';
import { EditSVG } from '../../../svgComponents/editSVG';

const roleMessages = defineMessages({
    applicant: {
        id: '_.your_company.applicant',
        defaultMessage: 'Applicant'
    },
    guarantor: {
        id: '_.your_company.guarantor',
        defaultMessage: 'Guarantor'
    },
    director: {
        id: '_.your_company.director',
        defaultMessage: 'Director'
    },
    shareholder: {
        id: '_.your_company.shareholder',
        defaultMessage: 'Shareholder'
    },
    partner: {
        id: '_.your_company.partner',
        defaultMessage: 'Partner'
    },
    beneficial_owner: {
        id: '_.your_company.beneficial_owner',
        defaultMessage: 'Beneficial owner'
    },
    other: {
        id: '_.your_company.other',
        defaultMessage: 'Other'
    }
});

export function PersonCard({
    firstName,
    lastName,
    roles,
    onClick,
    isGuarantor
}: {
    firstName?: string;
    lastName?: string;
    roles?: definitions['person']['roles'];
    onClick: () => void;
    isGuarantor?: boolean;
}) {
    const noPgSplitTest = useSplitTests('no_pg_test_2025');
    return (
        <div className="rounded-s bg-structure-100 shadow-black-s">
            <div className="space-y-s p-l">
                <div className="flex justify-between">
                    <FormattedName firstName={firstName} lastName={lastName} />
                    <button
                        className="border-none bg-transparent fill-primary-10 stroke-primary-10 hover:cursor-pointer"
                        data-testid="edit_person"
                        onClick={onClick}
                    >
                        <EditSVG className="h-4 w-4" />
                    </button>
                </div>
                <div className="text-s font-reg" data-testid="person-roles">
                    <PersonCardRoles roles={roles} />
                </div>
            </div>
            {isGuarantor && !noPgSplitTest && (
                <div className="flex p-l">
                    <CheckShieldSVG className="mr-2xs" />
                    <Text className="text-s font-med">
                        <FormattedMessage
                            id="_.your_company.personal_guarantor"
                            defaultMessage="Personal guarantor"
                        />
                    </Text>
                </div>
            )}
        </div>
    );
}

function PersonCardRoles({ roles }: { roles: definitions['person']['roles'] }) {
    const noPgSplitTest = useSplitTests('no_pg_test_2025');

    if (!roles) return <></>;

    let filtered_roles = roles.filter((role) => role !== 'authorised_person');
    if (noPgSplitTest) {
        filtered_roles = filtered_roles.filter((role) => role !== 'guarantor');
    }
    return (
        <>
            {filtered_roles.map((role, i) => {
                if (i + 1 === filtered_roles.length) {
                    return (
                        // Disabled as it's a unique cases mapping through for translations
                        // eslint-disable-next-line local/no-spread-messages
                        <FormattedMessage key={role} {...roleMessages[role]} />
                    );
                }

                return (
                    // Disabled as it's a unique cases mapping through for translations
                    // eslint-disable-next-line local/no-spread-messages
                    <FormattedMessage key={role} {...roleMessages[role]}>
                        {(text) => `${text}, `}
                    </FormattedMessage>
                );
            })}
        </>
    );
}
